import {
   buildExamQuestionPutPayload,
   normalizeExamQuestionRecord,
   toPositiveInt,
} from "./examQuestionFormat";
import { extractTicketNumber } from "./examTicketsOfficialImport";

export const THEORY_TICKETS_MAX = 24;

const OFFICIAL_TYPES = new Set([
   "official_first_6_tickets_ro_ru_correct_answers",
   "official_tickets_ro_ru_correct_answers",
   "official_practice_tickets_ro_ru_correct_answers",
]);

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function firstNonEmpty(...values) {
   for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
   }
   return "";
}

function normalizeTicketCode(ticketNo) {
   const n = toPositiveInt(ticketNo);
   return n ? `P${n}` : "";
}

function normalizeBKey(value) {
   const text = String(value ?? "").trim().toUpperCase();
   if (!text) return "";
   if (/^B\d+$/.test(text)) return text;
   const digits = text.replace(/^B/, "");
   const n = toPositiveInt(digits);
   return n ? `B${n}` : text;
}

function parseQuestionText(raw) {
   if (isObject(raw?.question)) {
      return {
         ro: firstNonEmpty(raw.question.ro, raw.question.RO),
         ru: firstNonEmpty(raw.question.ru, raw.question.RU),
      };
   }

   return {
      ro: firstNonEmpty(raw?.textRo, raw?.text_ro, raw?.text),
      ru: firstNonEmpty(raw?.textRu, raw?.text_ru),
   };
}

function parseOfficialAnswers(raw) {
   const rows = safeList(raw?.answers)
      .map((answer, index) => ({
         index: toPositiveInt(answer?.index) ?? index + 1,
         ro: firstNonEmpty(answer?.ro, answer?.RO),
         ru: firstNonEmpty(answer?.ru, answer?.RU),
         correct: answer?.correct === true,
      }))
      .sort((left, right) => Number(left.index) - Number(right.index));

   const answers = rows.map((answer) => ({
      ro: answer.ro || answer.ru,
      ru: answer.ru || answer.ro,
   }));

   let correctAnswer = toPositiveInt(raw?.correctAnswer?.index);
   let found = Boolean(correctAnswer);

   if (!found) {
      const flagged = rows.find((answer) => answer.correct);
      if (flagged) {
         correctAnswer = flagged.index;
         found = true;
      }
   }

   if (!found) {
      const flaggedIndex = rows.findIndex((answer) => answer.correct);
      if (flaggedIndex >= 0) {
         correctAnswer = flaggedIndex + 1;
         found = true;
      }
   }

   if (
      correctAnswer &&
      (correctAnswer < 1 || correctAnswer > Math.max(answers.length, 1))
   ) {
      correctAnswer = null;
      found = false;
   }

   return {
      answers,
      correctAnswer: correctAnswer || 1,
      correctAnswerFallbackUsed: !found,
   };
}

function normalizeOfficialQuestion(raw, order) {
   if (!isObject(raw)) {
      throw new Error(`Întrebarea #${order} nu este validă.`);
   }

   const text = parseQuestionText(raw);
   if (!text.ro && !text.ru) {
      throw new Error(`Întrebarea ${raw?.b || order}: lipsește textul.`);
   }

   const { answers, correctAnswer, correctAnswerFallbackUsed } =
      parseOfficialAnswers(raw);

   if (answers.length < 2) {
      throw new Error(
         `Întrebarea ${raw?.b || order}: sunt necesare minimum 2 răspunsuri.`,
      );
   }

   const draft = {
      textRo: text.ro,
      textRu: text.ru,
      image: "",
      answers,
      correctAnswer,
      categoryId: null,
   };

   return {
      order,
      bKey: normalizeBKey(raw?.b ?? raw?.B),
      qid: toPositiveInt(raw?.qid ?? raw?.qId ?? raw?.questionId),
      confidence: String(raw?.correctConfidence ?? raw?.confidence ?? "").trim(),
      officialHintRo: String(raw?.officialHintRo ?? "").trim(),
      correctAnswerFallbackUsed,
      draft,
      // P1–P24: pe server răspunsul corect e de la 0
      payload: buildExamQuestionPutPayload(draft, { ticketType: "PRACTICE" }),
   };
}

function normalizeOfficialTicket(raw, index = 0) {
   if (!isObject(raw)) return null;

   const ticketNo =
      toPositiveInt(raw?.ticket ?? raw?.ticketNo ?? raw?.ticket_no ?? raw?.bilet) ||
      extractTicketNumber(raw?.ticketName ?? raw?.name) ||
      extractTicketNumber(raw?.ticketCode ?? raw?.code) ||
      index + 1;

   if (!ticketNo || ticketNo < 1 || ticketNo > THEORY_TICKETS_MAX) {
      throw new Error(
         `Biletul ${ticketNo ?? "?"} este invalid. Se acceptă doar P1–P${THEORY_TICKETS_MAX}.`,
      );
   }

   const questions = safeList(raw?.questions).map((question, questionIndex) =>
      normalizeOfficialQuestion(question, questionIndex + 1),
   );

   if (!questions.length) {
      throw new Error(`P${ticketNo}: nu are întrebări.`);
   }

   return {
      ticketNo,
      ticketCode: normalizeTicketCode(ticketNo),
      ticketName:
         firstNonEmpty(raw?.ticketName, raw?.name) || `Practice P${ticketNo}`,
      ticketId: toPositiveInt(raw?.ticketId ?? raw?.ticket_id),
      questions,
   };
}

export function isOfficialTheoryImport(parsed) {
   if (!isObject(parsed) && !Array.isArray(parsed)) return false;

   const type = String(parsed?.type ?? "").trim();
   if (OFFICIAL_TYPES.has(type) || type.includes("official")) return true;

   const tickets = safeList(parsed?.tickets);
   if (!tickets.length) return false;

   const firstQuestion = tickets[0]?.questions?.[0];
   return Boolean(isObject(firstQuestion?.question));
}

export function parseTheoryOfficialImport(parsed) {
   const errors = [];
   const type = String(parsed?.type ?? "").trim();
   const rule = String(
      parsed?.rule ?? parsed?.criticalRule ?? parsed?.applyRule ?? "",
   ).trim();

   const tickets = safeList(parsed?.tickets)
      .map((ticket, index) => {
         try {
            return normalizeOfficialTicket(ticket, index);
         } catch (error) {
            errors.push(error?.message || `Bilet invalid la index ${index + 1}.`);
            return null;
         }
      })
      .filter(Boolean)
      .sort((left, right) => left.ticketNo - right.ticketNo);

   if (!tickets.length) {
      errors.push(
         'Fișier invalid. Aștept format oficial: { "type", "rule", "tickets": [{ "ticket": 1, "questions": [...] }] }.',
      );
   }

   const ticketNumbers = new Set(tickets.map((ticket) => ticket.ticketNo));
   if (ticketNumbers.size !== tickets.length) {
      errors.push("Fișierul conține bilete duplicate (același număr P).");
   }

   if (tickets.some((ticket) => ticket.ticketNo > THEORY_TICKETS_MAX)) {
      errors.push(`Se acceptă doar biletele P1–P${THEORY_TICKETS_MAX}.`);
   }

   const questionsTotal = tickets.reduce(
      (sum, ticket) => sum + ticket.questions.length,
      0,
   );
   const reviewCount = tickets.reduce(
      (sum, ticket) =>
         sum +
         ticket.questions.filter(
            (question) =>
               question.correctAnswerFallbackUsed ||
               String(question.confidence).toLowerCase() === "review",
         ).length,
      0,
   );

   return {
      type: type || "official_practice_tickets_ro_ru_correct_answers",
      rule,
      tickets,
      errors,
      summary: {
         ticketsTotal: tickets.length,
         questionsTotal,
         reviewCount,
         ticketRange:
            tickets.length > 0
               ? `P${tickets[0].ticketNo}–P${tickets[tickets.length - 1].ticketNo}`
               : "",
      },
   };
}

export function resolvePracticeTicket(importTicket, dbTickets = []) {
   const rows = safeList(dbTickets);
   if (!importTicket || !rows.length) return null;

   if (importTicket.ticketId) {
      const byId = rows.find(
         (row) => Number(row?.id) === Number(importTicket.ticketId),
      );
      if (byId) return byId;
   }

   const ticketNo = toPositiveInt(importTicket.ticketNo);
   if (!ticketNo) return null;

   const code = normalizeTicketCode(ticketNo).toLowerCase();
   const byCode = rows.find((row) => {
      const name = String(row?.name ?? "").trim().toLowerCase();
      const extracted = extractTicketNumber(row?.name);
      return (
         extracted === ticketNo ||
         name === code.toLowerCase() ||
         name === `practice ${code.toLowerCase()}` ||
         name.includes(` ${code.toLowerCase()}`) ||
         name.endsWith(code.toLowerCase())
      );
   });

   return byCode || null;
}

export function orderQuestionsByIds(questions, questionIds = []) {
   const ids = safeList(questionIds)
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);
   const byId = new Map(
      safeList(questions)
         .map((question) => {
            const id = toPositiveInt(question?.id);
            return id ? [id, question] : null;
         })
         .filter(Boolean),
   );

   const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
   const leftovers = safeList(questions).filter(
      (question) => !ids.includes(Number(question?.id)),
   );

   return [...ordered, ...leftovers];
}

export function buildTheoryOfficialApplicationPlan(
   importTicket,
   existingQuestions,
   questionIds = [],
) {
   const orderedExisting = orderQuestionsByIds(existingQuestions, questionIds);
   const updates = [];

   for (const importQuestion of importTicket.questions) {
      const position = importQuestion.order - 1;
      const target = orderedExisting[position] || null;

      if (!target?.id) {
         updates.push({
            order: importQuestion.order,
            bKey: importQuestion.bKey,
            status: "missing",
            error: `Nu există întrebare la poziția ${importQuestion.order} (${importQuestion.bKey || "—"}).`,
         });
         continue;
      }

      const current = normalizeExamQuestionRecord(target);
      const image = firstNonEmpty(current?.image, importQuestion.draft?.image);

      updates.push({
         order: importQuestion.order,
         bKey: importQuestion.bKey,
         questionId: target.id,
         confidence: importQuestion.confidence,
         status: "ready",
         payload: {
            ...importQuestion.payload,
            image,
         },
      });
   }

   return {
      ticketNo: importTicket.ticketNo,
      ticketCode: importTicket.ticketCode,
      updates,
      readyCount: updates.filter((row) => row.status === "ready").length,
      missingCount: updates.filter((row) => row.status === "missing").length,
   };
}
